import React, { Suspense, lazy } from "react";
import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";

// Legacy /products/:slug → /produto/:slug redirect (preserves slug + querystring)
const LegacyProductRedirect = () => {
  const { slug } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/produto/${slug ?? ""}${search ?? ""}`} replace />;
};

// ── URL casing normalization ────────────────────────────────────────────────
// Google indexed /ciencia/pH-fisiologico-da-pele (uppercase H) while the
// sitemap ships the lowercase URL — same page, two URLs. Any pathname holding
// an uppercase ASCII letter redirects to its lowercase form, preserving the
// querystring and hash. Only the PATH is lowercased; query values keep their
// case (tokens, UTMs, affiliate/referral codes) and so does the hash (OAuth
// tokens land there). Wraps the whole route table — a mixed-case URL that would
// otherwise match a dynamic route (/ciencia/:slug) must normalize BEFORE
// resolving, so putting this on the `*` catch-all alone would not work.
const CaseNormalizer = ({ children }: { children: React.ReactNode }) => {
  const { pathname, search, hash } = useLocation();
  if (/[A-Z]/.test(pathname)) {
    return <Navigate to={`${pathname.toLowerCase()}${search}${hash}`} replace />;
  }
  return <>{children}</>;
};
import { CartProvider } from "@/contexts/CartContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { UserContextProvider } from "@/contexts/UserContextProvider";
import { HealthcareProfessionalProvider } from "@/contexts/HealthcareProfessionalContext";
import { AffiliateProvider } from "@/contexts/AffiliateContext";
import { ProfessionalModeProvider } from "@/components/enterprise/ProfessionalModeToggle";
import { PageTracker } from "@/components/PageTracker";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ProtectedDebugRoute } from "@/components/routes/ProtectedDebugRoute";
import { ProtectedAdminRoute, ProtectedProfessionalRoute, ProtectedConsumerRoute } from "@/components/routes/ProtectedRoutes";
import { useReferralCapture } from "@/hooks/useReferralCapture";
import { captureAttribution } from "@/lib/attribution";

// Production-only: no debug components
const useCrmDebugShortcut = () => {};

// Critical: Index loads synchronously for FCP/LCP
import Index from "./pages/Index";

// Lazy-loaded pages - code split for reduced initial bundle
const Links = lazy(() => import("./pages/Links"));
const Materiais = lazy(() => import("./pages/Materiais"));
const Shop = lazy(() => import("./pages/Shop"));
const ProductPage = lazy(() => import("./pages/ProductPage"));
const Kits = lazy(() => import("./pages/Kits"));
const About = lazy(() => import("./pages/About"));
const DisruptoresEndocrinosSobre = lazy(() => import("./pages/sobre/DisruptoresEndocrinos"));
const Science = lazy(() => import("./pages/Science"));
const B2B = lazy(() => import("./pages/B2B"));
const Clinicas = lazy(() => import("./pages/Clinicas"));
const ClinicaDetail = lazy(() => import("./pages/ClinicaDetail"));
const Quiz = lazy(() => import("./pages/Quiz"));
const NicheLanding = lazy(() => import("./pages/NicheLanding"));
const LandingBalsamoOzonizadoOncologico = lazy(() => import("./pages/LandingBalsamoOzonizadoOncologico"));
const Resultado = lazy(() => import("./pages/Resultado"));
const Auth = lazy(() => import("./pages/Auth"));
const Profile = lazy(() => import("./pages/Profile"));
const Diario = lazy(() => import("./pages/Diario"));
const MinhaPele = lazy(() => import("./pages/MinhaPele"));
const Relatorio = lazy(() => import("./pages/Relatorio"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Contact = lazy(() => import("./pages/Contact"));
const ContatoProfissional = lazy(() => import("./pages/ContatoProfissional"));
const Reviews = lazy(() => import("./pages/Reviews"));
const Avaliacoes = lazy(() => import("./pages/Avaliacoes"));
const Filosofia = lazy(() => import("./pages/Filosofia"));
const Privacy = lazy(() => import("./pages/policies/Privacy"));
const Terms = lazy(() => import("./pages/policies/Terms"));
const Returns = lazy(() => import("./pages/policies/Returns"));
const Frete = lazy(() => import("./pages/policies/Frete"));
const TermosIndicacao = lazy(() => import("./pages/policies/TermosIndicacao"));
const ExclusaoDeDados = lazy(() => import("./pages/policies/ExclusaoDeDados"));
const Garantia = lazy(() => import("./pages/policies/Garantia"));
const NotFound = lazy(() => import("./pages/NotFound"));
const HidratanteQuimioterapia = lazy(() => import("./pages/HidratanteQuimioterapia"));
const PeleRessecada = lazy(() => import("./pages/PeleRessecada"));
const HidratantePeDiabetico = lazy(() => import("./pages/HidratantePeDiabetico"));
const PedidoConfirmado = lazy(() => import("./pages/PedidoConfirmado"));

// Ciência & Bioativos
const Bioativos = lazy(() => import("./pages/Bioativos"));
const NossaCiencia = lazy(() => import("./pages/NossaCiencia"));
const SegurancaTestes = lazy(() => import("./pages/SegurancaTestes"));
const EstudosEvidencias = lazy(() => import("./pages/EstudosEvidencias"));
const PeleSensivelOncologica = lazy(() => import("./pages/PeleSensivelOncologica"));
const Glossario = lazy(() => import("./pages/Glossario"));
const HidratacaoMatriz = lazy(() => import("./pages/ciencia/HidratacaoMatriz"));
const BarreiraLipidica = lazy(() => import("./pages/ciencia/BarreiraLipidica"));
const ConfortoSuavidade = lazy(() => import("./pages/ciencia/ConfortoSuavidade"));
const AntioxidantesProtecao = lazy(() => import("./pages/ciencia/AntioxidantesProtecao"));
const DesreguladoresEndocrinos = lazy(() => import("./pages/ciencia/DesreguladoresEndocrinos"));
const BarreiraCutanea = lazy(() => import("./pages/ciencia/BarreiraCutanea"));
const TEWLPerdaTransepidermica = lazy(() => import("./pages/ciencia/TEWLPerdaTransepidermica"));
const PHFisiologicoPele = lazy(() => import("./pages/ciencia/PHFisiologicoPele"));
const ReparoCutaneo = lazy(() => import("./pages/ciencia/ReparoCutaneo"));
const PeleSensivelFisiologia = lazy(() => import("./pages/ciencia/PeleSensivelFisiologia"));
const SistemaMultissurfactante = lazy(() => import("./pages/ciencia/SistemaMultissurfactante"));
const MicrobiotaCutanea = lazy(() => import("./pages/ciencia/MicrobiotaCutanea"));
const DermocosmeticaCientifica = lazy(() => import("./pages/ciencia/DermocosmeticaCientifica"));
const FormulacaoAltaTolerabilidade = lazy(() => import("./pages/ciencia/FormulacaoAltaTolerabilidade"));
const Ceramidas = lazy(() => import("./pages/Ceramidas"));

// Bioativos individuais
const AcidoHialuronico = lazy(() => import("./pages/bioativos/AcidoHialuronico"));
const UreiaPage = lazy(() => import("./pages/bioativos/Ureia"));
const Alantoina = lazy(() => import("./pages/bioativos/Alantoina"));
const AloeVera = lazy(() => import("./pages/bioativos/AloeVera"));
const OleoGirassol = lazy(() => import("./pages/bioativos/OleoGirassol"));
const OleoRosaMosqueta = lazy(() => import("./pages/bioativos/OleoRosaMosqueta"));
const ManteigaKarite = lazy(() => import("./pages/bioativos/ManteigaKarite"));
const Camomila = lazy(() => import("./pages/bioativos/Camomila"));
const ChaVerdeEgcg = lazy(() => import("./pages/bioativos/ChaVerdeEgcg"));
const Propolis = lazy(() => import("./pages/bioativos/Propolis"));
const OleoGirassolOzonizado = lazy(() => import("./pages/bioativos/OleoGirassolOzonizado"));
const Lavanda = lazy(() => import("./pages/bioativos/Lavanda"));
const PalmarosaPage = lazy(() => import("./pages/bioativos/Palmarosa"));
const Omega3 = lazy(() => import("./pages/bioativos/Omega3"));
const Omega6 = lazy(() => import("./pages/bioativos/Omega6"));
const Omega9 = lazy(() => import("./pages/bioativos/Omega9"));

// Para Clínicas
const Hospitais = lazy(() => import("./pages/Hospitais"));
const Protocolos = lazy(() => import("./pages/Protocolos"));
const Treinamentos = lazy(() => import("./pages/Treinamentos"));
const Parcerias = lazy(() => import("./pages/Parcerias"));

// Ambiente Profissional
const ProfissionalGate = lazy(() => import("./pages/profissional/ProfissionalGate"));
const ProfissionalRegistro = lazy(() => import("./pages/profissional/ProfissionalRegistro"));
const ProfissionalDashboard = lazy(() => import("./pages/profissional/ProfissionalDashboard"));
const ProtocolosIndex = lazy(() => import("./pages/profissional/protocolos/ProtocolosIndex"));
const ProtocoloEspecialidade = lazy(() => import("./pages/profissional/protocolos/ProtocoloEspecialidade"));
const MateriaisTecnicos = lazy(() => import("./pages/profissional/MateriaisTecnicos"));
const Certificacoes = lazy(() => import("./pages/profissional/Certificacoes"));
const TreinamentosProfissionais = lazy(() => import("./pages/profissional/TreinamentosProfissionais"));

// Nossa Essência
const Sustentabilidade = lazy(() => import("./pages/Sustentabilidade"));

// Rotinas
const RotinasIndex = lazy(() => import("./pages/rotinas/RotinasIndex"));
const RotinaEssencial = lazy(() => import("./pages/rotinas/RotinaEssencial"));
const RotinaRessecamento = lazy(() => import("./pages/rotinas/RotinaRessecamento"));
const RotinaSensibilidade = lazy(() => import("./pages/rotinas/RotinaSensibilidade"));
const RotinaReparo = lazy(() => import("./pages/rotinas/RotinaReparo"));
const RotinaMaosPes = lazy(() => import("./pages/rotinas/RotinaMaosPes"));
const RotinaLimpeza = lazy(() => import("./pages/rotinas/RotinaLimpeza"));
const RotinaOncologia = lazy(() => import("./pages/rotinas/RotinaOncologia"));

// Oncologia Hub
const OncologiaHub = lazy(() => import("./pages/oncologia/OncologiaHub"));
// OncoCare consolidado em OncologiaHub (/oncologia) — rota /onco-care agora redireciona
const Radiodermite = lazy(() => import("./pages/oncologia/Radiodermite"));
const SindromeMaoPe = lazy(() => import("./pages/oncologia/SindromeMaoPe"));
const PeleRessecadaQuimioterapia = lazy(() => import("./pages/oncologia/PeleRessecadaQuimioterapia"));
const PruridoQuimioterapia = lazy(() => import("./pages/oncologia/PruridoQuimioterapia"));
const ErupcaoAcneiformeEgfr = lazy(() => import("./pages/oncologia/ErupcaoAcneiformeEgfr"));
const CuidadosPeleTratamentoCancer = lazy(() => import("./pages/oncologia/CuidadosPeleTratamentoCancer"));

// Programas
const Programas = lazy(() => import("./pages/Programas"));
const Revendedores = lazy(() => import("./pages/Revendedores"));

const Pedidos = lazy(() => import("./pages/Pedidos"));
const Favoritos = lazy(() => import("./pages/Favoritos"));

// Afiliados
const ProgramaAfiliados = lazy(() => import("./pages/afiliados/ProgramaAfiliados"));
const AfiliadoCadastro = lazy(() => import("./pages/afiliados/AfiliadoCadastro"));
const AfiliadoDashboard = lazy(() => import("./pages/afiliados/AfiliadoDashboard"));
const TermosAfiliados = lazy(() => import("./pages/afiliados/TermosAfiliados"));

// Utilitários
const MapaDoSite = lazy(() => import("./pages/MapaDoSite"));

// Blog
const BlogSaboneteSemDisruptores = lazy(() => import("./pages/blog/SaboneteSemDisruptores"));
const BlogHidratanteSemDisruptores = lazy(() => import("./pages/blog/HidratanteSemDisruptores"));
const BlogIndex = lazy(() => import("./pages/blog/BlogIndex"));
const BlogPost = lazy(() => import("./pages/blog/BlogPost"));
const SegmentPage = lazy(() => import("./pages/para/SegmentPage"));
const SearchResultsPage = lazy(() => import("./pages/SearchResultsPage"));

// Landing Pages
const PeleSensivel = lazy(() => import("./pages/landings/PeleSensivel"));
const SaudeECuidadoDaPele = lazy(() => import("./pages/landings/SaudeECuidadoDaPele"));
const PeleFragilizada = lazy(() => import("./pages/landings/PeleFragilizada"));
const PeleExtremamenteRessecada = lazy(() => import("./pages/landings/PeleExtremamenteRessecada"));
const PeleSensibilizadaPorTratamentos = lazy(() => import("./pages/landings/PeleSensibilizadaPorTratamentos"));
const PeleEmRegeneracao = lazy(() => import("./pages/landings/PeleEmRegeneracao"));
const PeleReativaEIntolerante = lazy(() => import("./pages/landings/PeleReativaEIntolerante"));
const PeleSensivelNoDiaADia = lazy(() => import("./pages/landings/PeleSensivelNoDiaADia"));
const LinhaClinic = lazy(() => import("./pages/landings/LinhaClinic"));
const LinhaCare = lazy(() => import("./pages/landings/LinhaCare"));
const ComoCuidarDaPeleSensivel = lazy(() => import("./pages/landings/ComoCuidarDaPeleSensivel"));
const HidratanteSemFragrancia = lazy(() => import("./pages/landings/HidratanteSemFragrancia"));
const Amostra = lazy(() => import("./pages/landings/Amostra"));
const ProtocoloOncologia = lazy(() => import("./pages/landings/protocolos/ProtocoloOncologia"));
const ProtocoloGeriatria = lazy(() => import("./pages/landings/protocolos/ProtocoloGeriatria"));
const ProtocoloPosOperatorio = lazy(() => import("./pages/landings/protocolos/ProtocoloPosOperatorio"));
const ProtocoloMaternidade = lazy(() => import("./pages/landings/protocolos/ProtocoloMaternidade"));
const ProtocoloEsteticaSegura = lazy(() => import("./pages/landings/protocolos/ProtocoloEsteticaSegura"));
const ProtocoloCirurgiaPlastica = lazy(() => import("./pages/landings/protocolos/ProtocoloCirurgiaPlastica"));
const ProtocoloBariatrica = lazy(() => import("./pages/landings/protocolos/ProtocoloBariatrica"));

// Estética/Dermato cluster
const EsteticaDermatoHub = lazy(() => import("./pages/estetica-dermato/EsteticaDermatoHub"));
const PosLaser = lazy(() => import("./pages/estetica-dermato/PosLaser"));
const PosPeeling = lazy(() => import("./pages/estetica-dermato/PosPeeling"));
const PosBotox = lazy(() => import("./pages/estetica-dermato/PosBotox"));
const PosPreenchimento = lazy(() => import("./pages/estetica-dermato/PosPreenchimento"));
const PeleSensibilizadaProcedimentos = lazy(() => import("./pages/estetica-dermato/PeleSensibilizadaProcedimentos"));
const RecuperacaoBarreiraCutanea = lazy(() => import("./pages/estetica-dermato/RecuperacaoBarreiraCutanea"));
const LiftingDermocosmetico = lazy(() => import("./pages/estetica-dermato/LiftingDermocosmetico"));

// Geriatria/ILPI cluster
const GeriatriaILPIHub = lazy(() => import("./pages/geriatria-ilpi/GeriatriaILPIHub"));
const PeleFragilizadaIdosos = lazy(() => import("./pages/geriatria-ilpi/PeleFragilizadaIdosos"));
const PeleSecaNoIdoso = lazy(() => import("./pages/geriatria-ilpi/PeleSecaNoIdoso"));
const PrevencaoLesaoPressao = lazy(() => import("./pages/geriatria-ilpi/PrevencaoLesaoPressao"));
const CuidadosCutaneosILPI = lazy(() => import("./pages/geriatria-ilpi/CuidadosCutaneosILPI"));

// Pé Diabético/Vascular cluster
const VascularPeDiabeticoHub = lazy(() => import("./pages/vascular-pe-diabetico/VascularPeDiabeticoHub"));
const PeDiabeticoCuidadosPele = lazy(() => import("./pages/vascular-pe-diabetico/PeDiabeticoCuidadosPele"));
const FissurasPlantares = lazy(() => import("./pages/vascular-pe-diabetico/FissurasPlantares"));
const PeleRessecadaNosPes = lazy(() => import("./pages/vascular-pe-diabetico/PeleRessecadaNosPes"));
const CuidadoDermocosmeticoNosPes = lazy(() => import("./pages/vascular-pe-diabetico/CuidadoDermocosmeticoNosPes"));

// Maternidade/Hormonal cluster
const MaternidadeHub = lazy(() => import("./pages/maternidade/MaternidadeHub"));
const PeleNaGravidez = lazy(() => import("./pages/maternidade/PeleNaGravidez"));
const PelePosParto = lazy(() => import("./pages/maternidade/PelePosParto"));
const PeleSensivelHormonal = lazy(() => import("./pages/maternidade/PeleSensivelHormonal"));
const PeleNoClimaterio = lazy(() => import("./pages/maternidade/PeleNoClimaterio"));

// Clean Beauty cluster
const CleanBeautyHub = lazy(() => import("./pages/clean-beauty/CleanBeautyHub"));
const DermocosmeticosAltaTolerabilidade = lazy(() => import("./pages/clean-beauty/DermocosmericosAltaTolerabilidade"));
const FormulacoesSeguras = lazy(() => import("./pages/clean-beauty/FormulacoesSeguras"));
const IngredientesDermatologicos = lazy(() => import("./pages/clean-beauty/IngredientesDermatologicos"));
const PhFisiologico = lazy(() => import("./pages/clean-beauty/PhFisiologico"));
const SurfactantesSuaves = lazy(() => import("./pages/clean-beauty/SurfactantesSuaves"));

// Dashboard
const DashboardHome = lazy(() => import("./pages/dashboard/DashboardHome"));
const DashboardJourney = lazy(() => import("./pages/dashboard/DashboardJourney"));
const DashboardQuiz = lazy(() => import("./pages/dashboard/DashboardQuiz"));
const DashboardFriction = lazy(() => import("./pages/dashboard/DashboardFriction"));
const DashboardContent = lazy(() => import("./pages/dashboard/DashboardContent"));
const DashboardCRM = lazy(() => import("./pages/dashboard/DashboardCRM"));
const DashboardObservability = lazy(() => import("./pages/dashboard/DashboardObservability"));
const DashboardActions = lazy(() => import("./pages/dashboard/DashboardActions"));
const DashboardIntelligence = lazy(() => import("./pages/dashboard/DashboardIntelligence"));
const DashboardProducts = lazy(() => import("./pages/dashboard/DashboardProducts"));
const DashboardAds = lazy(() => import("./pages/dashboard/DashboardAds"));
const DashboardClarity = lazy(() => import("./pages/dashboard/DashboardClarity"));
const DashboardMarketplaces = lazy(() => import("./pages/dashboard/DashboardMarketplaces"));

// Auth
const AuthCallback = lazy(() => import("./pages/auth/AuthCallback"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));

// TikTok Shop
const TikTokShopCallback = lazy(() => import("./pages/TikTokShopCallback"));

// SECURITY: Debug pages are only loaded in development mode
const CrmDebug = import.meta.env.DEV ? lazy(() => import("./pages/debug/CrmDebug")) : () => null;
const CrmHealthDebug = import.meta.env.DEV ? lazy(() => import("./pages/debug/CrmHealthDebug")) : () => null;
const CrmErrorsDebug = import.meta.env.DEV ? lazy(() => import("./pages/debug/CrmErrorsDebug")) : () => null;
const CrmEventStatus = import.meta.env.DEV ? lazy(() => import("./pages/debug/CrmEventStatus")) : () => null;
const ShopifyDebug = import.meta.env.DEV ? lazy(() => import("./pages/debug/ShopifyDebug")) : () => null;
const SeoDebug = import.meta.env.DEV ? lazy(() => import("./pages/debug/SeoDebug")) : () => null;

const queryClient = new QueryClient();

// Minimal loading fallback - matches background color
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

// Production global hooks only
const GlobalHooks = () => {
  useReferralCapture();
  // Persist first-touch attribution (utm_*/gclid/fbclid) + rebuild _fbc cookie.
  React.useEffect(() => {
    captureAttribution();
  }, []);
  return null;
};

// Protected route for authenticated users (any role)
function ProtectedAuthRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
}

// Route table, shared with the SSG entry (src/entry-ssg.tsx).
export const AppRoutes = () => (
            <Routes>
              {/* ══════════════════════════════════════════════════════
                  CORE COMMERCE
                  ══════════════════════════════════════════════════════ */}
              <Route path="/" element={<Index />} />
              <Route path="/links" element={<Links />} />
              <Route path="/materiais" element={<Materiais />} />
              <Route path="/busca" element={<SearchResultsPage />} />
              <Route path="/loja" element={<Shop />} />
              <Route path="/loja/:category" element={<Shop />} />
              <Route path="/produto/:slug" element={<ProductPage />} />
              <Route path="/kits" element={<Kits />} />
              <Route path="/sobre" element={<About />} />
              <Route path="/sobre/disruptores-endocrinos" element={<Navigate to="/ciencia/desreguladores-endocrinos" replace />} />
              <Route path="/ciencia-e-tecnologia" element={<Science />} />
              <Route path="/b2b" element={<B2B />} />
              <Route path="/clinicas" element={<Clinicas />} />
              <Route path="/clinicas/:slug" element={<ClinicaDetail />} />
              <Route path="/quiz" element={<Quiz />} />
              <Route path="/resultado" element={<Resultado />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/auth/reset-password" element={<ResetPassword />} />
              <Route path="/perfil" element={<ProtectedConsumerRoute><Profile /></ProtectedConsumerRoute>} />
              <Route path="/pedidos" element={<ProtectedAuthRoute><Pedidos /></ProtectedAuthRoute>} />
              <Route path="/favoritos" element={<ProtectedAuthRoute><Favoritos /></ProtectedAuthRoute>} />
              <Route path="/diario" element={<ProtectedAuthRoute><Diario /></ProtectedAuthRoute>} />
              <Route path="/minha-pele" element={<ProtectedAuthRoute><MinhaPele /></ProtectedAuthRoute>} />
              <Route path="/relatorio" element={<ProtectedAuthRoute><Relatorio /></ProtectedAuthRoute>} />

              {/* ══════════════════════════════════════════════════════
                  CIÊNCIA & BIOATIVOS
                  ══════════════════════════════════════════════════════ */}
              <Route path="/bioativos" element={<Bioativos />} />
              <Route path="/bioativos/acido-hialuronico" element={<AcidoHialuronico />} />
              <Route path="/bioativos/ureia" element={<UreiaPage />} />
              <Route path="/bioativos/alantoina" element={<Alantoina />} />
              <Route path="/bioativos/aloe-vera" element={<AloeVera />} />
              <Route path="/bioativos/oleo-de-girassol" element={<OleoGirassol />} />
              <Route path="/bioativos/oleo-de-rosa-mosqueta" element={<OleoRosaMosqueta />} />
              <Route path="/bioativos/manteiga-de-karite" element={<ManteigaKarite />} />
              <Route path="/bioativos/camomila" element={<Camomila />} />
              <Route path="/bioativos/cha-verde-egcg" element={<ChaVerdeEgcg />} />
              <Route path="/bioativos/propolis" element={<Propolis />} />
              <Route path="/bioativos/oleo-de-girassol-ozonizado" element={<OleoGirassolOzonizado />} />
              <Route path="/bioativos/lavanda" element={<Lavanda />} />
              <Route path="/bioativos/palmarosa" element={<PalmarosaPage />} />
              <Route path="/bioativos/omega-3" element={<Omega3 />} />
              <Route path="/bioativos/omega-6" element={<Omega6 />} />
              <Route path="/bioativos/omega-9" element={<Omega9 />} />
              <Route path="/nossa-ciencia" element={<NossaCiencia />} />
              <Route path="/seguranca-testes" element={<SegurancaTestes />} />
              <Route path="/estudos-evidencias" element={<EstudosEvidencias />} />
              <Route path="/glossario" element={<Glossario />} />
              <Route path="/ciencia/hidratacao-matriz" element={<HidratacaoMatriz />} />
              <Route path="/ciencia/desreguladores-endocrinos" element={<DesreguladoresEndocrinos />} />
              <Route path="/blog" element={<BlogIndex />} />
              <Route path="/blog/sabonete-sem-disruptores-endocrinos" element={<BlogSaboneteSemDisruptores />} />
              <Route path="/blog/hidratante-sem-disruptores-endocrinos" element={<BlogHidratanteSemDisruptores />} />
              <Route path="/blog/o-que-e-foliculite-como-tratar" element={<Navigate to="/blog/o-que-e-foliculite-cuidados-suaves" replace />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/para/:slug" element={<SegmentPage />} />
              <Route path="/ceramidas" element={<Ceramidas />} />
              <Route path="/ciencia/barreira-lipidica" element={<BarreiraLipidica />} />
              <Route path="/ciencia/conforto-suavidade" element={<ConfortoSuavidade />} />
              <Route path="/ciencia/antioxidantes-protecao" element={<AntioxidantesProtecao />} />
              <Route path="/ciencia/barreira-cutanea" element={<BarreiraCutanea />} />
              <Route path="/ciencia/tewl-perda-transepidermica-de-agua" element={<TEWLPerdaTransepidermica />} />
              <Route path="/ciencia/ph-fisiologico-da-pele" element={<PHFisiologicoPele />} />
              <Route path="/ciencia/reparo-cutaneo" element={<ReparoCutaneo />} />
              <Route path="/ciencia/pele-sensivel-fisiologia" element={<PeleSensivelFisiologia />} />
              <Route path="/ciencia/sistema-multissurfactante-suave" element={<SistemaMultissurfactante />} />
              <Route path="/ciencia/microbiota-cutanea" element={<MicrobiotaCutanea />} />
              <Route path="/ciencia/dermocosmetica-cientifica" element={<DermocosmeticaCientifica />} />
              <Route path="/ciencia/formulacao-de-alta-tolerabilidade" element={<FormulacaoAltaTolerabilidade />} />

              {/* ══════════════════════════════════════════════════════
                  PARA CLÍNICAS & PROFISSIONAIS
                  ══════════════════════════════════════════════════════ */}
              <Route path="/hospitais" element={<Hospitais />} />
              <Route path="/protocolos" element={<Protocolos />} />
              <Route path="/treinamentos" element={<Treinamentos />} />
              <Route path="/parcerias" element={<Parcerias />} />
              <Route path="/profissionais-saude" element={<ProfissionalGate />} />
              <Route path="/profissional/protocolos" element={<ProtocolosIndex />} />
              <Route path="/profissional/protocolos/:specialty" element={<ProtocoloEspecialidade />} />
              <Route path="/profissional/materiais" element={<MateriaisTecnicos />} />
              <Route path="/profissional/certificacoes" element={<Certificacoes />} />
              <Route path="/profissional/treinamentos" element={<TreinamentosProfissionais />} />
              <Route path="/profissional/dashboard" element={<ProtectedAdminRoute><ProfissionalDashboard /></ProtectedAdminRoute>} />
              <Route path="/profissional/registro" element={<ProfissionalRegistro />} />
              <Route path="/contato-profissional" element={<ContatoProfissional />} />

              {/* ══════════════════════════════════════════════════════
                  NOSSA ESSÊNCIA & INSTITUCIONAL
                  ══════════════════════════════════════════════════════ */}
              <Route path="/sustentabilidade" element={<Sustentabilidade />} />
              <Route path="/filosofia" element={<Filosofia />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/contato" element={<Contact />} />
              <Route path="/reviews" element={<Reviews />} />
              <Route path="/avaliacoes" element={<Avaliacoes />} />

              {/* ══════════════════════════════════════════════════════
                  ROTINAS
                  ══════════════════════════════════════════════════════ */}
              <Route path="/rotinas" element={<RotinasIndex />} />
              <Route path="/rotinas/essencial" element={<RotinaEssencial />} />
              <Route path="/rotinas/ressecamento" element={<RotinaRessecamento />} />
              <Route path="/rotinas/sensibilidade" element={<RotinaSensibilidade />} />
              <Route path="/rotinas/reparo" element={<RotinaReparo />} />
              <Route path="/rotinas/maos-pes" element={<RotinaMaosPes />} />
              <Route path="/rotinas/limpeza" element={<RotinaLimpeza />} />
              <Route path="/rotinas/oncologia" element={<RotinaOncologia />} />

              {/* ══════════════════════════════════════════════════════
                  CLINICAL HUBS
                  ══════════════════════════════════════════════════════ */}
              <Route path="/oncologia" element={<OncologiaHub />} />
              <Route path="/oncologia/radiodermite" element={<Radiodermite />} />
              <Route path="/oncologia/sindrome-mao-pe" element={<SindromeMaoPe />} />
              <Route path="/oncologia/pele-ressecada-quimioterapia" element={<PeleRessecadaQuimioterapia />} />
              <Route path="/oncologia/prurido-quimioterapia" element={<PruridoQuimioterapia />} />
              <Route path="/oncologia/erupcao-acneiforme-egfr" element={<ErupcaoAcneiformeEgfr />} />
              <Route path="/oncologia/cuidados-pele-tratamento-cancer" element={<CuidadosPeleTratamentoCancer />} />

              <Route path="/estetica-dermato" element={<EsteticaDermatoHub />} />
              <Route path="/estetica-dermato/pos-laser" element={<PosLaser />} />
              <Route path="/estetica-dermato/pos-peeling" element={<PosPeeling />} />
              <Route path="/estetica-dermato/pos-botox" element={<PosBotox />} />
              <Route path="/estetica-dermato/pos-preenchimento" element={<PosPreenchimento />} />
              <Route path="/estetica-dermato/pele-sensibilizada-procedimentos" element={<PeleSensibilizadaProcedimentos />} />
              <Route path="/estetica-dermato/recuperacao-barreira-cutanea" element={<RecuperacaoBarreiraCutanea />} />
              <Route path="/estetica-dermato/lifting-dermocosmetico" element={<LiftingDermocosmetico />} />

              <Route path="/geriatria-ilpi" element={<GeriatriaILPIHub />} />
              <Route path="/geriatria-ilpi/pele-fragilizada-idosos" element={<PeleFragilizadaIdosos />} />
              <Route path="/geriatria-ilpi/pele-seca-no-idoso" element={<PeleSecaNoIdoso />} />
              <Route path="/geriatria-ilpi/prevencao-lesao-pressao" element={<PrevencaoLesaoPressao />} />
              <Route path="/geriatria-ilpi/cuidados-cutaneos-ilpi" element={<CuidadosCutaneosILPI />} />

              <Route path="/vascular-pe-diabetico" element={<VascularPeDiabeticoHub />} />
              <Route path="/vascular-pe-diabetico/pe-diabetico-cuidados-pele" element={<PeDiabeticoCuidadosPele />} />
              <Route path="/vascular-pe-diabetico/fissuras-plantares" element={<FissurasPlantares />} />
              <Route path="/vascular-pe-diabetico/pele-ressecada-nos-pes" element={<PeleRessecadaNosPes />} />
              <Route path="/vascular-pe-diabetico/cuidado-dermocosmetico-nos-pes" element={<CuidadoDermocosmeticoNosPes />} />

              <Route path="/maternidade" element={<MaternidadeHub />} />
              <Route path="/maternidade/pele-na-gravidez" element={<PeleNaGravidez />} />
              <Route path="/maternidade/pele-pos-parto" element={<PelePosParto />} />
              <Route path="/maternidade/pele-sensivel-hormonal" element={<PeleSensivelHormonal />} />
              <Route path="/maternidade/pele-no-climaterio" element={<PeleNoClimaterio />} />

              <Route path="/clean-beauty" element={<CleanBeautyHub />} />
              <Route path="/clean-beauty/dermocosmeticos-de-alta-tolerabilidade" element={<DermocosmeticosAltaTolerabilidade />} />
              <Route path="/clean-beauty/formulacoes-seguras" element={<FormulacoesSeguras />} />
              <Route path="/clean-beauty/ingredientes-dermatologicos" element={<IngredientesDermatologicos />} />
              <Route path="/clean-beauty/ph-fisiologico" element={<PhFisiologico />} />
              <Route path="/clean-beauty/surfactantes-suaves" element={<SurfactantesSuaves />} />

              {/* ══════════════════════════════════════════════════════
                  PROGRAMAS
                  ══════════════════════════════════════════════════════ */}
              <Route path="/programas" element={<Programas />} />
              <Route path="/revendedores" element={<Revendedores />} />
              <Route path="/afiliados" element={<ProgramaAfiliados />} />
              <Route path="/afiliados/cadastro" element={<AfiliadoCadastro />} />
              <Route path="/afiliados/dashboard" element={<ProtectedAuthRoute><AfiliadoDashboard /></ProtectedAuthRoute>} />
              <Route path="/afiliados/termos" element={<TermosAfiliados />} />

              {/* ══════════════════════════════════════════════════════
                  LANDING PAGES
                  ══════════════════════════════════════════════════════ */}
              <Route path="/pele-sensivel" element={<PeleSensivel />} />
              <Route path="/saude-e-cuidado-da-pele" element={<SaudeECuidadoDaPele />} />
              <Route path="/pele-fragilizada" element={<PeleFragilizada />} />
              <Route path="/pele-extremamente-ressecada" element={<PeleExtremamenteRessecada />} />
              <Route path="/pele-sensibilizada-por-tratamentos" element={<PeleSensibilizadaPorTratamentos />} />
              <Route path="/pele-em-regeneracao" element={<PeleEmRegeneracao />} />
              <Route path="/pele-reativa-e-intolerante" element={<PeleReativaEIntolerante />} />
              <Route path="/pele-sensivel-no-dia-a-dia" element={<PeleSensivelNoDiaADia />} />
              <Route path="/linha-clinic" element={<LinhaClinic />} />
              <Route path="/linha-care" element={<LinhaCare />} />
              <Route path="/como-cuidar-da-pele-sensivel" element={<ComoCuidarDaPeleSensivel />} />
              <Route path="/hidratante-sem-fragrancia" element={<HidratanteSemFragrancia />} />
              <Route path="/amostra" element={<Amostra />} />

              {/* ══════════════════════════════════════════════════════
                  PROTOCOLOS B2B
                  ══════════════════════════════════════════════════════ */}
              <Route path="/protocolos/oncologia" element={<ProtocoloOncologia />} />
              <Route path="/protocolos/geriatria" element={<ProtocoloGeriatria />} />
              <Route path="/protocolos/pos-operatorio" element={<ProtocoloPosOperatorio />} />
              <Route path="/protocolos/maternidade" element={<ProtocoloMaternidade />} />
              <Route path="/protocolos/estetica-segura" element={<ProtocoloEsteticaSegura />} />
              <Route path="/protocolos/cirurgia-plastica" element={<ProtocoloCirurgiaPlastica />} />
              <Route path="/protocolos/bariatrica" element={<ProtocoloBariatrica />} />

              {/* ══════════════════════════════════════════════════════
                  POLÍTICAS — Canonical URLs
                  ══════════════════════════════════════════════════════ */}
              <Route path="/politicas/privacidade" element={<Privacy />} />
              <Route path="/politicas/termos-de-servico" element={<Terms />} />
              <Route path="/politicas/frete-envio" element={<Frete />} />
              <Route path="/politicas/troca-devolucao" element={<Returns />} />
              <Route path="/politicas/indicacao" element={<TermosIndicacao />} />
              <Route path="/exclusao-de-dados" element={<ExclusaoDeDados />} />
              <Route path="/garantia" element={<Garantia />} />
              <Route path="/politicas/garantia" element={<Navigate to="/garantia" replace />} />

              {/* ══════════════════════════════════════════════════════
                  PEDIDO CONFIRMADO — Shopify checkout return
                  ══════════════════════════════════════════════════════ */}
              <Route path="/pedido-confirmado" element={<PedidoConfirmado />} />

              {/* ══════════════════════════════════════════════════════
                  NICHE LANDING PAGES (orphaned but kept for SEO eval)
                  ══════════════════════════════════════════════════════ */}
              <Route path="/barreira-melasma" element={<NicheLanding />} />
              <Route path="/inverno-baixa-umidade" element={<NicheLanding />} />
              <Route path="/viagem-neve-altitude" element={<NicheLanding />} />
              <Route path="/pele-jovem-sensivel" element={<NicheLanding />} />
              <Route path="/pos-sol" element={<NicheLanding />} />
              <Route path="/dermatites-pele-reativa" element={<NicheLanding />} />
              <Route path="/pos-barba-homens" element={<NicheLanding />} />
              <Route path="/profissionais-saude-epi" element={<NicheLanding />} />
              <Route path="/balsamo-ozonizado-pele-oncologica" element={<LandingBalsamoOzonizadoOncologico />} />

              {/* ══════════════════════════════════════════════════════
                  UTILITÁRIOS
                  ══════════════════════════════════════════════════════ */}
              <Route path="/mapa-do-site" element={<MapaDoSite />} />

              {/* ══════════════════════════════════════════════════════
                  301 REDIRECTS — Duplicate/legacy routes
                  ══════════════════════════════════════════════════════ */}
              <Route path="/consulta-guiada/resultado" element={<Navigate to="/resultado" replace />} />
              <Route path="/profissional" element={<Navigate to="/profissionais-saude" replace />} />
              <Route path="/revendedor" element={<Navigate to="/revendedores" replace />} />
              <Route path="/quero-ser-revendedor" element={<Navigate to="/revendedores" replace />} />
              <Route path="/programa-de-afiliados" element={<Navigate to="/afiliados" replace />} />
              {/* /onco-care consolidado em /oncologia (conteúdo duplicado; keyword PT mais forte) */}
              <Route path="/onco-care" element={<Navigate to="/oncologia" replace />} />
              <Route path="/maternidade-segura" element={<Navigate to="/maternidade" replace />} />
              <Route path="/menopausa-climaterio" element={<Navigate to="/maternidade/pele-no-climaterio" replace />} />
              <Route path="/pos-procedimentos" element={<Navigate to="/estetica-dermato" replace />} />
              <Route path="/pele-sensivel-oncologica" element={<Navigate to="/oncologia" replace />} />
              <Route path="/logistica-reversa" element={<Navigate to="/sustentabilidade" replace />} />
              {/* Policy aliases → canonical */}
              <Route path="/politica-de-privacidade" element={<Navigate to="/politicas/privacidade" replace />} />
              <Route path="/politicas/termos" element={<Navigate to="/politicas/termos-de-servico" replace />} />
              <Route path="/trocas-e-devolucoes" element={<Navigate to="/politicas/troca-devolucao" replace />} />
              <Route path="/politicas/entrega" element={<Navigate to="/politicas/frete-envio" replace />} />
              <Route path="/politicas/frete" element={<Navigate to="/politicas/frete-envio" replace />} />
              {/* Legacy product & content redirects */}
              <Route path="/hidratante-corporal-uso-pessoal" element={<Navigate to="/produto/hidratante-corporal-dermoconstrutor-80g" replace />} />
              <Route path="/pagina/disruptores-endocrinos.html" element={<Navigate to="/ciencia/desreguladores-endocrinos" replace />} />
              <Route path="/creme-hidrantante-dermoconstrutor" element={<Navigate to="/produto/hidratante-corporal-dermoconstrutor-80g" replace />} />
              <Route path="/creme-hiper-hidratante-para-as-maos" element={<Navigate to="/produto/hidratante-para-as-maos-dermoconstrutor-80g" replace />} />
              <Route path="/creme-hiper-hidratante-para-os-pes" element={<Navigate to="/produto/hidratante-para-os-pes-dermoconstrutor-80g" replace />} />
              <Route path="/balsamo-hidratante" element={<Navigate to="/produto/balsamo-hidratante-corpo-e-rosto-30ml" replace />} />
              <Route path="/hidratante-corporal" element={<Navigate to="/produto/hidratante-corporal-dermoconstrutor-80g" replace />} />
              {/* Real Shopify handle — the short one does not exist */}
              <Route path="/necessaire" element={<Navigate to="/produto/necessaire-exclusiva-bordada-nat-hof-compacta-pratica-e-elegante-para-dermocosmeticos" replace />} />
              {/* Indexed legacy PDP URL for the 1kg (BBH01, R$199) */}
              <Route path="/produto/hidratante-corporal-1000g" element={<Navigate to="/produto/hidratante-corporal-dermoconstrutor-1kg" replace />} />
              <Route path="/kit-hidratacao-e-limpeza" element={<Navigate to="/produto/kit-hidratacao-e-limpeza-suave" replace />} />
              <Route path="/kit-hidratacao-diaria-essencial" element={<Navigate to="/produto/kit-hidratacao-diaria-essencial" replace />} />
              <Route path="/kit-regeneracao-e-hidratacao-intensiva" element={<Navigate to="/produto/kit-regeneracao-e-hidratacao-intensiva" replace />} />
              <Route path="/kit-nahof" element={<Navigate to="/loja" replace />} />
              <Route path="/wpm" element={<Navigate to="/" replace />} />
              <Route path="/wpm/*" element={<Navigate to="/" replace />} />
              {/* URL casing normalization → handled by the CaseNormalizer wrapper
                  around <Routes> above (runs before any route matches). */}
              {/* ══════════════════════════════════════════════════════
                  DASHBOARD — ADMIN ONLY
                  ══════════════════════════════════════════════════════ */}
              <Route path="/dashboard" element={<ProtectedAdminRoute><DashboardHome /></ProtectedAdminRoute>} />
              <Route path="/dashboard/intelligence" element={<ProtectedAdminRoute><DashboardIntelligence /></ProtectedAdminRoute>} />
              <Route path="/dashboard/products" element={<ProtectedAdminRoute><DashboardProducts /></ProtectedAdminRoute>} />
              <Route path="/dashboard/journey" element={<ProtectedAdminRoute><DashboardJourney /></ProtectedAdminRoute>} />
              <Route path="/dashboard/quiz" element={<ProtectedAdminRoute><DashboardQuiz /></ProtectedAdminRoute>} />
              <Route path="/dashboard/friction" element={<ProtectedAdminRoute><DashboardFriction /></ProtectedAdminRoute>} />
              <Route path="/dashboard/content" element={<ProtectedAdminRoute><DashboardContent /></ProtectedAdminRoute>} />
              <Route path="/dashboard/crm" element={<ProtectedAdminRoute><DashboardCRM /></ProtectedAdminRoute>} />
              <Route path="/dashboard/observability" element={<ProtectedAdminRoute><DashboardObservability /></ProtectedAdminRoute>} />
              <Route path="/dashboard/actions" element={<ProtectedAdminRoute><DashboardActions /></ProtectedAdminRoute>} />
              <Route path="/dashboard/ads" element={<ProtectedAdminRoute><DashboardAds /></ProtectedAdminRoute>} />
              <Route path="/dashboard/clarity" element={<ProtectedAdminRoute><DashboardClarity /></ProtectedAdminRoute>} />
              <Route path="/dashboard/marketplaces" element={<ProtectedAdminRoute><DashboardMarketplaces /></ProtectedAdminRoute>} />

              {/* ══════════════════════════════════════════════════════
                  DEBUG — Protected, dev-only
                  ══════════════════════════════════════════════════════ */}
              {import.meta.env.DEV && (
                <>
                  <Route path="/debug/crm" element={<ProtectedDebugRoute><CrmDebug /></ProtectedDebugRoute>} />
                  <Route path="/debug/health-crm" element={<ProtectedDebugRoute><CrmHealthDebug /></ProtectedDebugRoute>} />
                  <Route path="/debug/crm-errors" element={<ProtectedDebugRoute><CrmErrorsDebug /></ProtectedDebugRoute>} />
                  <Route path="/debug/crm-event-status" element={<ProtectedDebugRoute><CrmEventStatus /></ProtectedDebugRoute>} />
                  <Route path="/debug/shopify" element={<ProtectedDebugRoute><ShopifyDebug /></ProtectedDebugRoute>} />
                  <Route path="/seo-debug" element={<ProtectedDebugRoute><SeoDebug /></ProtectedDebugRoute>} />
                </>
              )}
              {/* In production, redirect all debug routes */}
              {!import.meta.env.DEV && (
                <>
                  <Route path="/debug/*" element={<Navigate to="/" replace />} />
                  
                </>
              )}

              {/* Compatibility redirects — canonical URLs used by SEO/campaigns */}
              <Route path="/produtos" element={<Navigate to="/loja" replace />} />
              <Route path="/products/:slug" element={<LegacyProductRedirect />} />
              <Route path="/collections/todos-os-produtos" element={<Navigate to="/loja" replace />} />
              <Route path="/collections/:collection" element={<Navigate to="/loja" replace />} />

              <Route path="/quem-somos" element={<Navigate to="/filosofia" replace />} />

              <Route path="/hidratante-quimioterapia" element={<HidratanteQuimioterapia />} />
              <Route path="/pele-ressecada-o-que-fazer" element={<PeleRessecada />} />
              <Route path="/hidratante-pe-diabetico" element={<HidratantePeDiabetico />} />
              <Route path="/tiktok-shop/callback" element={<TikTokShopCallback />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
);

// Client-only widgets mount after the first commit so the first client render
// matches the SSG HTML exactly (hydrateRoot on pilot routes). Elsewhere this
// is one extra frame before toasts/cookie banner can appear — no visual change.
const ClientOnly = ({ children }: { children: React.ReactNode }) => {
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => {
    setMounted(true);
  }, []);
  return mounted ? <>{children}</> : null;
};

// One tree for the browser and the SSG entry: hydrateRoot needs the same
// component structure on both sides (useId/radix ids depend on it).
export const AppTree = ({
  Router,
  client,
}: {
  Router: React.ComponentType<{ children: React.ReactNode }>;
  client: QueryClient;
}) => (
  <QueryClientProvider client={client}>
    <AuthProvider>
      <HealthcareProfessionalProvider>
      <AffiliateProvider>
      <UserContextProvider>
      <ProfessionalModeProvider>
      <CartProvider>
      <TooltipProvider>
        {/* Production hooks run always */}
        <GlobalHooks />
        <ClientOnly>
          <Toaster />
          <Sonner />
        </ClientOnly>
        <Router>
          <ScrollToTop />
          <PageTracker />
          <Suspense fallback={<PageLoader />}>
            <CaseNormalizer>
            <AppRoutes />
            </CaseNormalizer>
          </Suspense>
        </Router>
      </TooltipProvider>
      </CartProvider>
      </ProfessionalModeProvider>
      </UserContextProvider>
      </AffiliateProvider>
      </HealthcareProfessionalProvider>
    </AuthProvider>
  </QueryClientProvider>
);

const App = () => <AppTree Router={BrowserRouter} client={queryClient} />;

const SentryApp = Sentry.withErrorBoundary(App, {
  fallback: ({ error, resetError }) => (
    <div style={{ padding: 40, fontFamily: 'system-ui', maxWidth: 600, margin: '0 auto', textAlign: 'center' }}>
      <h2>Algo deu errado</h2>
      <p style={{ color: '#666' }}>Nossa equipe foi notificada. Por favor, recarregue a página.</p>
      <button
        onClick={resetError}
        style={{ marginTop: 20, padding: '10px 20px', cursor: 'pointer' }}
      >
        Tentar novamente
      </button>
    </div>
  ),
});

export default SentryApp;
